import { DateTime } from 'luxon';

/**
 * Pick the timestamp for a brand-new log entry created from a page that was opened for a
 * particular day.
 *
 * A page such as the weigh-in editor is told which day it was opened for, as an ISO date
 * string in its navigation parameters. A new entry created there belongs on THAT day, not
 * on today — the day decides which bucket the entry lands in, which day header it appears
 * under, and whether the page treats it as today.
 *
 * The answer is built from `now` and only the calendar date is moved onto it. That ordering
 * is deliberate and load-bearing, not a style choice. `createGenericEntry` takes the entry's
 * `timeOffset_min` from the offset of the DateTime it is handed, so that DateTime has to
 * carry the device's own local offset. Building the answer from the parsed day instead would
 * hand the creator whatever zone the day string happened to carry — a `Z` suffix would store
 * the entry as UTC-offset — which is exactly the bug #5013 fixed. See
 * design-docs/sync-integrity.md, "A Log Entry's Two Time Fields Must Always Move Together".
 *
 * An absent or unparseable day means "today", which is `now` unchanged.
 *
 * @param dayISO the day the page was opened for, as an ISO date string, or nothing
 * @param now the device's current local time
 */
export function newEntryTimeForDay(dayISO: string | undefined, now: DateTime): DateTime {
  const day = DateTime.fromISO(dayISO);
  // if the day parameter is missing or unreadable, the entry belongs on today
  if (!day.isValid) {
    return now;
  }
  return now.set({ year: day.year, month: day.month, day: day.day });
}
